import {FC, ReactNode, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {ModalBody, ModalCloseButton, ModalFooter, ModalHeader, ModalTitle} from '@components/ui/modal-atoms'
import {Sheet, SheetClose, SheetContent, SheetTrigger, SheetXCloseButton} from '@components/ui/sheet'
import {Button} from '@components/ui/button'
import lightingTipsImg from '@assets/guidelines/lighting-tips.webp'
import compositionTipsImg from '@assets/guidelines/composition-tips.webp'
import photoUploadingImg from '@assets/guidelines/photo-uploading.webp'
import photoStylingImg from '@assets/guidelines/photo-styling.webp'
import homesBriefImg from '@assets/guidelines/homes-brief.webp'
import {GUIDELINE_URLS} from '@features/insights/constants'
import {Dialog, DialogClose, DialogContent} from '@components/ui/dialog'
import {useMediaQuery} from '@hooks/use-media-query'

type GuidelineKey = keyof typeof GUIDELINE_URLS

const GUIDELINES: {key: GuidelineKey; href: string; image: string; durationMinutes: number}[] = [
    {key: 'lighting_tips', href: GUIDELINE_URLS.lighting_tips, image: lightingTipsImg, durationMinutes: 3},
    {key: 'composition_tips', href: GUIDELINE_URLS.composition_tips, image: compositionTipsImg, durationMinutes: 2},
    {key: 'photo_uploading', href: GUIDELINE_URLS.photo_uploading, image: photoUploadingImg, durationMinutes: 1},
    {key: 'photo_styling', href: GUIDELINE_URLS.photo_styling, image: photoStylingImg, durationMinutes: 3},
    {key: 'homes_brief', href: GUIDELINE_URLS.homes_brief, image: homesBriefImg, durationMinutes: 3}
]
const buttonClassName =
    'bg-transparent border-none p-0 font-inherit text-inherit font-bold underline cursor-pointer hover:text-neutral-900'
const GuidelinesList: FC<{title: ReactNode}> = ({title}) => {
    const {t} = useTranslation()
    return (
        <>
            {title}
            <ul className="m-0 flex list-none flex-col gap-6 p-0">
                {GUIDELINES.map(({key, href, image, durationMinutes}) => (
                    <li key={key}>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex cursor-pointer items-center gap-4 text-inherit no-underline"
                        >
                            <img src={image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" />
                            <div className="flex min-w-0 flex-1 flex-col gap-1 [&>span:first-child]:text-sm [&>span:first-child]:font-medium [&>span:first-child]:text-neutral-900 [&>span:last-child]:text-sm [&>span:last-child]:text-neutral-600">
                                <span>{t(`insights:guidelines:${key}_title` as const)}</span>
                                <span>{t('insights:guidelines:duration', {count: durationMinutes})}</span>
                            </div>
                        </a>
                    </li>
                ))}
            </ul>
        </>
    )
}

export const AirbnbGuidelinesModal: FC<{children?: ReactNode}> = ({children}) => {
    const {t} = useTranslation()
    const isLarge = useMediaQuery({breakpoint: 'md'})
    const [isDesktopOpen, setIsDesktopOpen] = useState(false)

    if (isLarge) {
        const handleClose = () => setIsDesktopOpen(false)

        return (
            <>
                <button type="button" onClick={() => setIsDesktopOpen(true)} className={buttonClassName}>
                    {children}
                </button>
                {isDesktopOpen && (
                    <Dialog open={isDesktopOpen} onOpenChange={setIsDesktopOpen}>
                        <DialogContent className="w-[560px]" showCloseButton={false}>
                            <div className="grid grid-rows-[auto_minmax(0,1fr)_auto] h-full">
                                <ModalHeader>
                                    <DialogClose data-slot="dialog-close" render={<ModalCloseButton />} />
                                </ModalHeader>
                                <ModalBody className="gap-4 p-6">
                                    <GuidelinesList
                                        title={
                                            <h3 className="text-xl font-medium text-neutral-900">
                                                {t('insights:guidelines:title')}
                                            </h3>
                                        }
                                    />
                                </ModalBody>
                                <ModalFooter className="justify-end">
                                    <Button variant="primary" onClick={handleClose}>
                                        {t('commons:close')}
                                    </Button>
                                </ModalFooter>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </>
        )
    }

    return (
        <Sheet>
            <SheetTrigger
                render={
                    <button type="button" className={buttonClassName}>
                        {children}
                    </button>
                }
            />
            <SheetContent showCloseButton={false}>
                <ModalHeader>
                    <SheetXCloseButton />
                </ModalHeader>
                <ModalBody>
                    <GuidelinesList title={<ModalTitle>{t('insights:guidelines:title')}</ModalTitle>} />
                </ModalBody>
                <ModalFooter className="justify-end">
                    <SheetClose render={<Button variant="primary">{t('commons:close')}</Button>} />
                </ModalFooter>
            </SheetContent>
        </Sheet>
    )
}
